import Project from 'src/entities/project';
import { useCallback, useState } from 'react';
import PricingPlanFeature from 'src/entities/pricing/pricingPlanFeature';
import useFeatureIsAvailableForProject from '@projects/hooks/useFeatureIsAvailableForProject';
import { trackClickedPaywallChip } from 'src/services/tracker/trackers/paywall';

import useUpdateCommentsAreHiddenForReviewers from './useUpdateCommentsAreHiddenForReviewers';

const useHideCommentsForReviewers = ( { project }: { project: Project } ) => {
	const {
		available: isFeatureAvailable,
		planWithFeature: planWithRequiredFeature
	} = useFeatureIsAvailableForProject(
		project.id, PricingPlanFeature.LIMIT_REVIEWERS_PAST_COMMENTS_ACCESS
	);

	const currentUserRoleInWorkspace = project.workspace.currentUserRole;
	const canUpgradeSubscriptionPlan = currentUserRoleInWorkspace?.subscriptionPermissions.edit || false;

	const [
		isShowingSubscriptionPlanSelectionModal,
		setIsShowingSubscriptionPlanSelectionModal
	] = useState( false );

	const [ commentsHiddenForReviewers, setCommentsHiddenForReviewers ] = useState(
		!project.seePastCommentsEnabledForReviewers
	);

	const updateCommentsAreHiddenForReviewers = useUpdateCommentsAreHiddenForReviewers( {
		projectID: project.id
	} );

	const onHiddenForReviewersChange = useCallback( () => {
		const hiddenForReviewers = !commentsHiddenForReviewers;
		setCommentsHiddenForReviewers( hiddenForReviewers );
		updateCommentsAreHiddenForReviewers( hiddenForReviewers );
	}, [ commentsHiddenForReviewers, updateCommentsAreHiddenForReviewers ] );

	const showChangeSubscriptionPlanModalOnClick = !isFeatureAvailable && canUpgradeSubscriptionPlan;
	const showCantUpgradeSubscriptionTootlip = !isFeatureAvailable && !canUpgradeSubscriptionPlan;
	const cantUpgradeSubscriptionTootlip = showCantUpgradeSubscriptionTootlip
		? `This feature is not available. Request workspace owner to upgrade to ${planWithRequiredFeature?.name}.`
		: '';

	const onRequiredPlanBadgeClick = useCallback( () => {
		trackClickedPaywallChip( {
			planName: planWithRequiredFeature?.name,
			feature: 'Hide past comments for reviewers'
		} );

		setIsShowingSubscriptionPlanSelectionModal( true );
	}, [ setIsShowingSubscriptionPlanSelectionModal ] );

	const onSubscriptionPlanSelectionModalClose = useCallback( () => {
		setIsShowingSubscriptionPlanSelectionModal( false );
	}, [ setIsShowingSubscriptionPlanSelectionModal ] );

	return {
		commentsHiddenForReviewers,
		onHiddenForReviewersChange,
		isSwitchEnabled: isFeatureAvailable,
		showChangeSubscriptionPlanModalOnClick,
		planWithRequiredFeature,
		cantUpgradeSubscriptionTootlip,
		isShowingSubscriptionPlanSelectionModal,
		onRequiredPlanBadgeClick,
		onSubscriptionPlanSelectionModalClose
	};
};

export default useHideCommentsForReviewers;
